<template>
  <div class="apikey-manager" v-loading="loading">
    <!-- 页面标题 -->
    <WelcomeBanner title="API Key 管理" subtitle="管理您的 API Key，查看调用统计和日志">
      <template #right>
        <el-button type="primary" @click="showCreateDialog" icon="el-icon-plus">创建 API Key</el-button>
      </template>
    </WelcomeBanner>

    <!-- 统计概览 -->
    <div class="stats-overview" v-if="stats.total > 0">
      <StatCard :value="stats.total" label="总数" :gradient="'var(--stat-gradient-1)'" />
      <StatCard :value="stats.active" label="有效" :gradient="'var(--stat-gradient-2)'" :active="true" />
      <StatCard :value="stats.inactive" label="已禁用" :gradient="'var(--stat-gradient-3)'" :inactive="true" />
    </div>

    <!-- 筛选区 -->
    <BaseCard class="filter-section">
      <div class="filter-row">
        <el-input
          v-model="filters.keyword"
          placeholder="搜索 Key 备注"
          clearable
          style="width: 220px;"
          @clear="handleFilter"
          @keyup.enter.native="handleFilter"
          prefix-icon="el-icon-search"
        />
        <el-select v-model="filters.status" placeholder="状态" clearable style="width: 120px;">
          <el-option label="全部状态" value="" />
          <el-option label="有效" :value="1" />
          <el-option label="已禁用" :value="0" />
        </el-select>
        <el-button @click="handleReset" icon="el-icon-refresh-left">重置</el-button>
        <el-button type="primary" @click="handleFilter" icon="el-icon-search">搜索</el-button>
      </div>
    </BaseCard>

    <!-- API Key 列表 -->
    <BaseCard class="table-container">
      <div v-if="keyList.length > 0">
        <el-table :data="keyList" stripe style="width: 100%">
          <el-table-column prop="api_key_id" label="Key ID" width="220" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <code class="key-id">{{ row.api_key_id }}</code>
            </template>
          </el-table-column>
          <el-table-column prop="remark" label="备注" min-width="180">
            <template slot-scope="{ row }">
              <el-link type="primary" @click="showDetail(row)">{{ row.remark || '未命名' }}</el-link>
            </template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="90">
            <template slot-scope="{ row }">
              <el-tag :type="getStatusType(row.status)" size="small" effect="dark">
                {{ row.status === 1 ? '有效' : '禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="expire_time" label="过期时间" width="140">
            <template slot-scope="{ row }">
              <span :class="{ 'text-danger': isExpired(row.expire_time), 'text-muted': !row.expire_time }">
                {{ formatExpireTime(row.expire_time) }}
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="create_time" label="创建时间" width="140">
            <template slot-scope="{ row }">
              {{ formatTime(row.create_time) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="220" fixed="right">
            <template slot-scope="{ row }">
              <el-tooltip content="查看详情" placement="top">
                <el-button type="text" size="small" icon="el-icon-view" @click="showDetail(row)">详情</el-button>
              </el-tooltip>
              <el-tooltip content="查看日志" placement="top">
                <el-button type="text" size="small" icon="el-icon-document" @click="showLogs(row)">日志</el-button>
              </el-tooltip>
              <el-divider direction="vertical"></el-divider>
              <el-dropdown trigger="click" @command="(cmd) => handleCommand(cmd, row)">
                <el-button type="text" size="small">更多<i class="el-icon-arrow-down el-icon--right"></i></el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :command="'toggle'" :icon="row.status === 1 ? 'el-icon-circle-close' : 'el-icon-circle-check'">
                    {{ row.status === 1 ? '禁用' : '启用' }}
                  </el-dropdown-item>
                  <el-dropdown-item :command="'delete'" icon="el-icon-delete" class="danger-item">
                    删除
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div v-else>
        <el-empty description="暂无 API Key">
          <el-button type="primary" @click="showCreateDialog" icon="el-icon-plus">创建 API Key</el-button>
        </el-empty>
      </div>

      <!-- 分页 -->
      <div class="pagination-wrapper">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :total="pagination.total"
          :page-size="pagination.pageSize"
          :current-page="pagination.currentPage"
          @current-change="handlePageChange"
        />
      </div>
    </BaseCard>

    <!-- 创建对话框 -->
    <el-dialog title="创建 API Key" :visible.sync="createDialogVisible" width="500px" @close="handleDialogClose">
      <el-form ref="keyForm" :model="keyForm" :rules="keyRules" label-width="100px">
        <el-form-item label="备注" prop="remark">
          <el-input v-model="keyForm.remark" placeholder="请输入 API Key 备注" />
        </el-form-item>
        <el-form-item label="有效期" prop="expireDays">
          <el-input-number v-model="keyForm.expireDays" :min="1" :max="365" placeholder="天数" style="width: 150px;" />
          <span class="input-hint">天（不填则永久有效）</span>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="createDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleCreate">创建</el-button>
      </div>
    </el-dialog>

    <!-- 详情对话框 -->
    <el-dialog title="API Key 详情" :visible.sync="detailVisible" width="600px">
      <el-descriptions v-if="currentKey" :column="2" border>
        <el-descriptions-item label="Key ID" :span="2">
          <div class="key-id-display">
            <code>{{ currentKey.api_key_id }}</code>
            <el-button type="text" size="mini" icon="el-icon-document-copy" @click="copyText(currentKey.api_key_id)">复制</el-button>
          </div>
        </el-descriptions-item>
        <el-descriptions-item label="备注" :span="2">{{ currentKey.remark || '-' }}</el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag :type="getStatusType(currentKey.status)" size="small" effect="dark">
            {{ currentKey.status === 1 ? '有效' : '禁用' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="过期时间">{{ formatExpireTime(currentKey.expire_time) }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{ formatTime(currentKey.create_time) }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{ formatTime(currentKey.update_time) }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button type="primary" @click="goToLogs" icon="el-icon-document">查看调用日志</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getApiKeyList, getApiKeyStatsSummary, createApiKey, revokeApiKey, deleteApiKey } from '@/api/apiKey'
import BaseCard from '@/components/BaseCard.vue'
import StatCard from '@/components/StatCard.vue'
import WelcomeBanner from '@/components/WelcomeBanner.vue'

export default {
  name: 'ApiKeyManager',
  components: { BaseCard, StatCard, WelcomeBanner },
  data() {
    return {
      loading: false,
      keyList: [],
      filters: {
        keyword: '',
        status: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      stats: {
        total: 0,
        active: 0,
        inactive: 0
      },
      createDialogVisible: false,
      detailVisible: false,
      currentKey: null,
      keyForm: {
        remark: '',
        expireDays: null
      },
      keyRules: {
        remark: [{ max: 100, message: '备注最多100个字符', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.loadKeys()
  },
  methods: {
    async loadStats() {
      try {
        const data = await getApiKeyStatsSummary()
        this.stats.total = data?.total || 0
        this.stats.active = data?.active || 0
        this.stats.inactive = data?.inactive || 0
      } catch (error) {
        console.error('加载统计失败:', error)
      }
    },

    async loadKeys() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          page_size: this.pagination.pageSize,
          keyword: this.filters.keyword || undefined,
          status: this.filters.status ? parseInt(this.filters.status) : undefined
        }
        const data = await getApiKeyList(params)
        const list = data?.items || data?.list || data || []
        this.keyList = list
        this.pagination.total = data?.total || 0
        // 单独加载统计数据
        this.loadStats()
      } catch (error) {
        console.error('加载 API Key 列表失败:', error)
        this.$message.error('加载列表失败')
      } finally {
        this.loading = false
      }
    },

    getStatusType(status) {
      return status === 1 ? 'success' : 'danger'
    },

    formatExpireTime(time) {
      if (!time) return '永久有效'
      if (this.isExpired(time)) return '已过期'
      return this.formatTime(time)
    },

    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },

    handleCommand(command, row) {
      if (command === 'toggle') {
        this.handleToggleStatus(row)
      } else if (command === 'delete') {
        this.handleDelete(row)
      }
    },

    maskApiKey(key) {
      if (!key) return '-'
      if (key.length <= 8) return '****'
      return key.substring(0, 4) + '****' + key.substring(key.length - 4)
    },

    toggleShowKey(row) {
      row.showKey = !row.showKey
    },

    copyKey(row) {
      if (!row.api_key) return
      navigator.clipboard.writeText(row.api_key).then(() => {
        this.$message.success('已复制到剪贴板')
      }).catch(() => {
        this.$message.error('复制失败')
      })
    },

    isExpired(expireTime) {
      if (!expireTime) return false
      return new Date(expireTime) < new Date()
    },

    formatTime(time) {
      if (!time) return '-'
      return new Date(time).toLocaleString('zh-CN')
    },

    handleFilter() {
      this.pagination.currentPage = 1
      this.loadKeys()
    },

    handleReset() {
      this.filters = { keyword: '', status: '' }
      this.pagination.currentPage = 1
      this.loadKeys()
    },

    handlePageChange(page) {
      this.pagination.currentPage = page
      this.loadKeys()
    },

    showCreateDialog() {
      this.keyForm = { remark: '', expireDays: null }
      this.createDialogVisible = true
    },

    handleDialogClose() {
      this.$refs.keyForm?.resetFields()
    },

    async handleCreate() {
      try {
        await this.$refs.keyForm.validate()
        const data = {
          remark: this.keyForm.remark,
          expire_days: this.keyForm.expireDays
        }
        const result = await createApiKey(data)
        this.createDialogVisible = false
        this.$message.success('API Key 创建成功')
        
        // 显示完整的 API Key
        if (result && result.api_key) {
          this.$alert(`API Key 已创建，请妥善保管：\n${result.api_key}`, '创建成功', {
            confirmButtonText: '我知道了',
            customClass: 'api-key-alert'
          })
        }
        this.loadKeys()
      } catch (error) {
        if (error !== false) {
          console.error('创建失败:', error)
          this.$message.error('创建失败')
        }
      }
    },

    showDetail(row) {
      this.currentKey = row
      this.detailVisible = true
    },

    showLogs(row) {
      this.$router.push({ name: 'ApiKeyLog', query: { keyId: row.api_key_id } })
    },

    goToLogs() {
      if (this.currentKey) {
        this.detailVisible = false
        this.showLogs(this.currentKey)
      }
    },

    async handleToggleStatus(row) {
      try {
        const newStatus = row.status === 1 ? 0 : 1
        await this.$confirm(`确定要${newStatus === 1 ? '启用' : '禁用'}该 API Key 吗？`, '提示', {
          type: 'warning'
        })
        await revokeApiKey(row.api_key_id)
        this.$message.success(`API Key 已${newStatus === 1 ? '启用' : '禁用'}`)
        this.loadKeys()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('操作失败:', error)
          this.$message.error('操作失败')
        }
      }
    },

    async handleDelete(row) {
      try {
        await this.$confirm('确定要永久删除该 API Key 吗？此操作不可恢复。', '警告', {
          type: 'warning',
          confirmButtonText: '确定删除',
          cancelButtonText: '取消'
        })
        await deleteApiKey(row.api_key_id)
        this.$message.success('删除成功')
        this.loadKeys()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          this.$message.error('删除失败')
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.apikey-manager {
  padding: 16px;
  min-height: calc(100vh - 100px);
  background: linear-gradient(180deg, #f5f7fa 0%, #fff 100%);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 24px;
  background: linear-gradient(135deg, #818cf8 0%, #a78bfa 100%);
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(129, 140, 248, 0.22);

  .header-left {
    display: flex;
    flex-direction: column;
    gap: 4px;

    h2 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: #fff;
    }

    .subtitle {
      margin: 0;
      font-size: 13px;
      color: rgba(255,255,255,0.9);
    }
  }

  .el-button {
    height: 36px;
    background: rgba(255,255,255,0.12);
    color: #fff;
    border: none;
  }

  .el-button:hover { background: rgba(255,255,255,0.18); }
}

  .stats-overview {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
  }

.filter-section {
  background: #fff;
  padding: 12px 14px;
  border-radius: 10px;
  margin-bottom: 16px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

.filter-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.table-container {
  background: #fff;
  padding: 12px;
  border-radius: 10px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.06);
}

/* 共用卡片样式 */
.card {
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 6px 18px rgba(14,30,37,0.06);
  padding: 12px;
  transition: transform .15s ease, box-shadow .15s ease;
}
.card:hover { transform: translateY(-4px); box-shadow: 0 12px 30px rgba(14,30,37,0.08); }

/* 创建新 API Key 的卡片（可点击） */
.create-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 1px dashed rgba(96,100,208,0.16);
  background: linear-gradient(180deg, rgba(99,102,241,0.04), rgba(167,139,250,0.02));
  color: #4b4f9a;
  min-height: 88px;
  border-radius: 12px;
  cursor: pointer;
}
.create-card .plus {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg,#6366f1,#a78bfa);
  color: #fff;
  font-weight: 700;
}

/* API Key 行/卡片 */
.api-key-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px;
  border-radius: 8px;
  transition: background .12s ease, transform .12s ease;
}
.api-key-row:hover { background: rgba(100,116,255,0.03); transform: translateY(-2px); }

.key-badge {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 600;
  background: linear-gradient(135deg,#8b5cf6,#06b6d4);
  box-shadow: 0 6px 18px rgba(139,92,246,0.14);
}

/* 弹窗与代码展示区域样式微调 */
.el-dialog__wrapper .el-dialog {
  border-radius: 12px;
  overflow: hidden;
}
.api-key-display code,
.key-id code,
.key-value {
  background: #f7f8fb;
  border-radius: 6px;
  padding: 6px 8px;
}

.danger-text,
.danger-item { color: #f56c6c !important; }

.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 16px;
}

.danger-text,
.danger-item {
  color: var(--danger-color, #f56c6c) !important;

  &:hover {
    color: var(--danger-color, #f56c6c) !important;
  }
}

.key-id {
  font-family: 'Monaco', 'Menlo', monospace;
  font-size: 12px;
  color: var(--text-muted);
}

.key-id-display {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  code {
    font-family: 'Monaco', 'Menlo', monospace;
    font-size: 12px;
    background: var(--bg-page);
    padding: 4px 8px;
    border-radius: var(--radius-sm);
    flex: 1;
  }
}

.api-key-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.key-value {
  font-family: monospace;
  font-size: 13px;
  background: var(--bg-page);
  padding: 2px 6px;
  border-radius: var(--radius-sm);
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.api-key-display {
  display: flex;
  align-items: center;
  gap: 12px;

  code {
    font-family: monospace;
    font-size: 12px;
    background: var(--bg-page);
    padding: 8px;
    border-radius: var(--radius-sm);
    word-break: break-all;
    flex: 1;
  }
}

.text-muted {
  color: var(--text-muted);
}

.text-danger {
  color: var(--danger-color, #f56c6c);
}

.input-hint {
  color: var(--text-muted);
  font-size: 13px;
  margin-left: 8px;
}
</style>
